import { useCurrentReflection } from '@/hooks'
import { Button, Ellipsis, Modal, Toast } from '@seiue/ui'
import { Dropdown } from 'antd'
import { GrowpIcon } from 'packages/components/GrowpIcon'
import {
  SchoolPlugin,
  SourceEnum,
  PluginStatusEnum,
  pluginApi,
} from 'packages/sdks-next/growp'
import { useContext, useState } from 'react'
import styled from 'styled-components'

import { UserContextParam } from '../../utils/type'
import {
  UserContext,
  clickAppCardFunc,
  openApplySystemPlugin,
  openEditUserPlugin,
} from '../../utils/util'

import { TopTip } from './TopTip'

/**
 * 门户管理 应用列表-用户应用卡片
 *
 * @param param - 参数
 * @param param.schoolPlugin - 显示应用
 * @param param.isForground - 是否是前台
 * @returns 组件
 */
export const AppCard: React.FC<{
  schoolPlugin: SchoolPlugin
  isForground?: boolean
}> = ({ schoolPlugin, isForground }) => {
  const { api: enablePlugin } = pluginApi.useEnabledSchoolPlugin({
    reload: [pluginApi.querySchoolPluginTags, pluginApi.querySchoolPlugins],
  })

  const { api: deletePlugin } = pluginApi.useDeleteSchoolPlugin({
    reload: [pluginApi.querySchoolPluginTags, pluginApi.querySchoolPlugins],
  })

  const { api: pinnedPlugin } = pluginApi.usePinnedSchoolPlugin({
    reload: [
      pluginApi.queryEnabledSchoolPlugins,
      pluginApi.querySchoolPluginTags,
    ],
  })

  const reflection = useCurrentReflection()

  const { pinnedPluginIds, setPinnedPluginIds } = isForground
    ? useContext<UserContextParam>(UserContext)
    : { pinnedPluginIds: [], setPinnedPluginIds: () => null }

  const items = [
    {
      label: (
        <Button
          type="text"
          onClick={() =>
            schoolPlugin.plugin
              ? openApplySystemPlugin(schoolPlugin.plugin, schoolPlugin)
              : openEditUserPlugin(schoolPlugin)
          }
        >
          {'配置'}
        </Button>
      ),
      key: 'k-edit',
    },

    ...(schoolPlugin.status === PluginStatusEnum.Enabled
      ? [
          {
            label: (
              <Button
                type="text"
                onClick={() => {
                  Modal.confirm({
                    title: '停用应用',
                    content:
                      '正在停用该应用，停用后成员将无法使用该应用。确定停用吗？',
                    onOk: async () => {
                      await enablePlugin({
                        id: schoolPlugin.id,
                        enabled: false,
                      })
                      Toast.success('已停用应用')
                    },
                  })
                }}
              >
                {'停用'}
              </Button>
            ),
            key: 'k-stop',
          },
        ]
      : []),
    ...(schoolPlugin.status === PluginStatusEnum.NotEnabled
      ? [
          {
            label: (
              <Button
                type="text"
                onClick={async () => {
                  await enablePlugin({
                    id: schoolPlugin.id,
                    enabled: true,
                  })
                  Toast.success('已成功启用应用')
                }}
              >
                {'启用'}
              </Button>
            ),
            key: 'k-open',
          },
        ]
      : []),
    {
      label: (
        <Button
          style={{ color: 'red' }}
          type="text"
          onClick={() => {
            Modal.danger({
              title: '确认删除该应用吗?',
              content:
                '正在删除该应用，删除后应用所以数据将被清空，确认删除吗？',
              onOk: async () => {
                await deletePlugin(schoolPlugin.id)
                Toast.success('已删除应用')
              },
            })
          }}
        >
          {'删除'}
        </Button>
      ),
      key: 'k-del',
    },
  ]

  const [openDropdown, setOpenDropdown] = useState(false)

  return (
    <Layout>
      <TopButton
        className={isForground ? 'btn' : 'hiddenicon'}
        onClick={(e) => {
          e.stopPropagation()
          const pinned = !pinnedPluginIds.includes(schoolPlugin.id)
          if (pinnedPluginIds.length === 8 && pinned) {
            Toast.info('置顶应用已达到上限')
            return
          }

          pinnedPlugin({
            pinned: pinned,
            id: schoolPlugin.id,
          })

          const newIds = pinned
            ? [...pinnedPluginIds, schoolPlugin.id]
            : pinnedPluginIds.filter((id) => id !== schoolPlugin.id)
          setPinnedPluginIds(newIds)
        }}
      >
        {pinnedPluginIds.includes(schoolPlugin.id) ? '取消置顶' : '置顶应用'}
      </TopButton>

      <CardBox
        className={isForground ? 'cardbox' : 'nostyle'}
        $openDropdown={openDropdown}
        onClick={() =>
          clickAppCardFunc(schoolPlugin, isForground, reflection || undefined)
        }
      >
        {schoolPlugin.status === PluginStatusEnum.Enabled ? null : (
          <TopTip
            unOpen={schoolPlugin.status === PluginStatusEnum.NotEnabled}
          />
        )}
        <ItemBox>
          <ImgBox src={schoolPlugin.icon || '/defaultapp.svg'} />
          <DescBox>
            <TitleBox>
              <LabelBox>
                <Ellipsis text={schoolPlugin.label} />
              </LabelBox>
              <IconBox className={isForground ? 'hiddenicon' : 'icon'}>
                <Dropdown
                  menu={{ items }}
                  onOpenChange={(val) => setOpenDropdown(val)}
                >
                  <GrowpIcon
                    name="Gengduo"
                    size={17}
                  />
                </Dropdown>
              </IconBox>
            </TitleBox>
            <TypeTag>
              {schoolPlugin.plugin?.source === SourceEnum.System
                ? '单点登录'
                : '推荐应用'}
            </TypeTag>
          </DescBox>
        </ItemBox>
        <Ellipsis
          lineClamp={2}
          text={schoolPlugin.description}
          style={{ fontSize: 12, color: '#455766' }}
        />
      </CardBox>
    </Layout>
  )
}

const Layout = styled.div`
  .hiddenicon {
    display: none;
  }

  .btn {
    display: none;
  }

  .cardbox {
  }

  .nostyle {
  }

  &:hover {
    .btn {
      display: block;
      cursor: pointer;
    }
    .cardbox {
      margin-top: -20px;
    }
  }
`

const CardBox = styled.div<{ $openDropdown: boolean }>`
  width: 100%;
  height: 140px;
  border-radius: 8px;

  .hiddenicon {
    display: none;
  }

  .icon {
    display: ${(props) => (props.$openDropdown ? 'flex' : 'none')};
  }

  &:hover {
    .icon {
      display: flex;
    }
  }

  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 12px;
  border: 1px solid ${(props) => props.theme.border.greyNormal};
  cursor: pointer;
`

const ItemBox = styled.div`
  width: 100%;
  height: 56px;
  display: flex;
  flex-direction: row;
  padding: 0px;
  gap: 12px;
  align-self: stretch;
`

const ImgBox = styled.img`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  border: 1px solid ${(props) => props.theme.border.greyLight};
`

const DescBox = styled.div`
  height: 56px;
  display: flex;
  flex-direction: column;
  padding: 2px 0px;
  gap: 8px;
  flex-grow: 1;
`

const TitleBox = styled.div`
  height: 24px;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: ${(props) => props.theme.text._1};
  display: flex;
  justify-content: space-between;
`

const TypeTag = styled.div`
  width: fit-content;
  height: 20px;
  border-radius: 4px;
  padding: 0px 8px;
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
  background-color: ${(props) => props.theme.background._3};
  cursor: pointer;
  flex-shrink: 0;
`

const LabelBox = styled.div`
  max-width: 150px;
`

const IconBox = styled.div`
  border-radius: 6px;
  background-color: ${(props) => props.theme.background._3};
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const TopButton = styled.div`
  width: 64px;
  height: 20px;
  border-radius: 4px;
  align-items: center;
  padding: 0px 8px;
  color: ${(props) => props.theme.brand._1};
  background-color: ${(props) => props.theme.brand._5};
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
  flex-shrink: 0;
  position: relative;
  left: calc(100% - 68px);
  top: 4px;
`
